// src/pages/ContactUs.jsx

import React, { useEffect, useState } from "react";

import {
  ArrowRight,
  CalendarDays,
  Check,
  Clock3,
  Globe,
  Headphones,
  Leaf,
  Mail,
  MapPin,
  Phone,
  Settings,
  Users,
  Zap,
   Building2,
} from "lucide-react";


import ContactHero from "../image/contact-hero.png";
import OfficeImage from "../image/office-building.png";


/* =========================================================
   ENQUIRY TYPES
========================================================= */

const enquiryTypes = [
  "General Enquiry",
  "Hydroponic Farm Setup",
  "Greenhouse Project",
  "Smart Farming Technology",
  "Commercial Farming",
  "Partnership",
  "Training & Consultation",
];


/* =========================================================
   FEATURES
========================================================= */

const features = [
  {
    icon: <Headphones size={28} />,
    title: "Expert",
    text: "Consultation",
  },
  {
    icon: <Settings size={28} />,
    title: "Customized",
    text: "Solutions",
  },
  {
    icon: <Zap size={28} />,
    title: "Quick",
    text: "Response",
  },
  {
    icon: <Users size={28} />,
    title: "Dedicated",
    text: "Support",
  },
  {
    icon: <Globe size={28} />,
    title: "Business",
    text: "Collaboration",
  },
];


/* =========================================================
   BENEFITS
========================================================= */

const benefits = [
  {
    icon: <Leaf size={30} />,
    title: "Expert Guidance",
    text: "Get advice from our hydroponic specialists",
  },
  {
    icon: <Settings size={30} />,
    title: "Customized Solutions",
    text: "Tailored to your needs and budget",
  },
  {
    icon: <Zap size={30} />,
    title: "Project Support",
    text: "From planning to installation",
  },
  {
    icon: <Users size={30} />,
    title: "Long-Term Partnership",
    text: "We grow together for a better future",
  },
];


export default function ContactUs() {

  /* =======================================================
     FORM STATE
  ======================================================= */

  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    company: "",
    enquiryType: "",
    message: "",
    consent: false,
  });

  const [submitted, setSubmitted] = useState(false);


  /* =======================================================
     SCROLL REVEAL
  ======================================================= */

  useEffect(() => {
    const elements = document.querySelectorAll(".reveal");

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {

          if (entry.isIntersecting) {
            entry.target.classList.add("show");

            observer.unobserve(entry.target);
          }

        });
      },
      {
        threshold: 0.12,
      }
    );

    elements.forEach((element) => {
      observer.observe(element);
    });

    return () => {
      observer.disconnect();
    };
  }, []);


  /* =======================================================
     FORM CHANGE
  ======================================================= */

  const handleChange = (e) => {

    const {
      name,
      value,
      type,
      checked,
    } = e.target;

    setFormData((prev) => ({
      ...prev,
      [name]:
        type === "checkbox"
          ? checked
          : value,
    }));
  };


  /* =======================================================
     FORM SUBMIT
  ======================================================= */
const handleSubmit = async (e) => {
  e.preventDefault();

  if (!formData.consent) {
    alert("Please agree to be contacted regarding your enquiry.");
    return;
  }

  try {
    const response = await fetch(
      "https://polybionictechnologies.com/api/contact/enquiry",
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(formData),
      }
    );

    const data = await response.json();

    if (!response.ok) {
      throw new Error(data.message || "Something went wrong");
    }

    console.log("Enquiry submitted:", data);

    setSubmitted(true);

    setFormData({
      name: "",
      email: "",
      phone: "",
      company: "",
      enquiryType: "",
      message: "",
      consent: false,
    });

    setTimeout(() => {
      setSubmitted(false);
    }, 5000);
  } catch (error) {
    console.error("Enquiry Error:", error);
    alert(error.message || "Failed to submit enquiry");
  }
};

  return (
    <>

      <main className="bg-white text-[#10231c]">

        {/* =====================================================
            HERO
        ===================================================== */}

        <section className="relative overflow-hidden">

          <div className="relative min-h-[430px] lg:min-h-[450px]">

            {/* Background */}

            <img
              src={ContactHero}
              alt="Hydroponic farming"
              className="hero-image absolute inset-0 h-full w-full object-cover"
            />


            {/* Gradient */}

            <div className="hero-gradient absolute inset-0 bg-gradient-to-r from-white via-white/90 to-transparent lg:from-white lg:via-white/75 lg:to-transparent" />


            {/* Decorative glow */}

            <div className="pointer-events-none absolute right-[8%] top-[18%] h-32 w-32 rounded-full bg-green-300/20 blur-3xl animate-pulse" />

            <div
              className="pointer-events-none absolute bottom-[10%] right-[20%] h-24 w-24 rounded-full bg-emerald-400/20 blur-2xl"
              style={{
                animation:
                  "floatingOrb 5s ease-in-out infinite",
              }}
            />


            {/* Content */}

            <div className="relative z-10 mx-auto max-w-[1400px] px-5 py-8 lg:px-14 lg:py-10">

              {/* Breadcrumb */}

              <div className="mb-4 flex items-center gap-2 text-sm text-gray-600">

                <a
                  href="/"
                  className="transition hover:text-[#087f4f]"
                >
                  Home
                </a>

                <span>›</span>

                <span className="text-gray-800">
                  Contact Us
                </span>

              </div>


              <div className="max-w-[500px] hero-content">

                <p className="mb-2 text-sm font-bold uppercase tracking-[0.15em] text-[#16854f]">
                  Contact Us
                </p>


                <h1 className="hero-title text-4xl font-extrabold leading-[1.05] sm:text-5xl lg:text-[52px]">

                  Let's Grow

                  <br />

                  a Sustainable

                  <br />

                  Future{" "}

                  <span className="text-[#087f4f]">
                    Together
                  </span>

                </h1>


                <p className="hero-description mt-4 max-w-[470px] text-sm leading-6 text-gray-600 sm:text-base">

                  Have a question, project idea or business enquiry?

                  Our team is here to help you with expert guidance
                  and customized hydroponic solutions.

                </p>


                <div className="hero-buttons mt-6 flex flex-wrap gap-3">

                  <a
                    href="#enquiry"
                    className="group inline-flex items-center gap-2 rounded-full bg-[#087f4f] px-6 py-3 text-sm font-bold text-white shadow-sm transition-all duration-300 hover:-translate-y-1 hover:bg-[#066b42] hover:shadow-lg"
                  >

                    Send an Enquiry

                    <ArrowRight
                      size={16}
                      className="transition-transform duration-300 group-hover:translate-x-1"
                    />

                  </a>


                  <a
                    href="tel:+919876543210"
                    className="group inline-flex items-center gap-2 rounded-full border border-gray-300 bg-white/90 px-6 py-3 text-sm font-bold text-gray-700 transition-all duration-300 hover:-translate-y-1 hover:border-[#087f4f] hover:text-[#087f4f] hover:shadow-md"
                  >

                    <Phone
                      size={16}
                      className="transition-transform duration-300 group-hover:scale-110"
                    />

                    Talk to Our Experts

                  </a>

                </div>

              </div>

            </div>

          </div>


          {/* =================================================
              HERO FEATURE STRIP
          ================================================= */}

          <div className="border-y border-green-100 bg-white">

            <div className="mx-auto grid max-w-[1400px] grid-cols-2 lg:grid-cols-5">

              {features.map((item, index) => (

                <div
                  key={index}
                  className={`feature-card flex items-center justify-center gap-3 px-4 py-5 ${
                    index !== features.length - 1
                      ? "border-r border-green-100"
                      : ""
                  }`}
                  style={{
                    animationDelay: `${index * 0.12}s`,
                  }}
                >

                  <div className="feature-icon text-[#087f4f]">
                    {item.icon}
                  </div>

                  <div>

                    <p className="text-sm font-bold">
                      {item.title}
                    </p>

                    <p className="text-xs text-gray-500">
                      {item.text}
                    </p>

                  </div>

                </div>

              ))}

            </div>

          </div>

        </section>


        {/* =====================================================
            CONTACT + ENQUIRY
        ===================================================== */}

        <section
          id="enquiry"
          className="reveal py-14 lg:py-16"
        >

          <div className="mx-auto max-w-[1400px] px-5 lg:px-10">

            <div className="grid gap-10 lg:grid-cols-[1fr_1.05fr]">


              {/* =================================================
                  LEFT
              ================================================= */}

              <div>

                <p className="mb-2 text-sm font-bold uppercase tracking-[0.15em] text-[#16854f]">
                  Get In Touch
                </p>

                <h2 className="text-3xl font-extrabold sm:text-4xl">
                  We're Here to Help
                </h2>

                <p className="mt-3 max-w-[620px] leading-7 text-gray-600">

                  Whether you are a farmer, business, investor or
                  just curious about hydroponic farming, feel free
                  to reach out. We'd love to hear from you and
                  explore how we can work together.

                </p>


                {/* Contact Cards */}

                <div className="mt-8 grid gap-4 sm:grid-cols-2">


                  {/* Phone */}

                  <div className="contact-card rounded-xl border border-gray-200 bg-white p-5 shadow-sm">

                    <div className="contact-icon flex h-11 w-11 items-center justify-center rounded-full bg-[#087f4f] text-white">
                      <Phone size={21} />
                    </div>

                    <h3 className="mt-4 font-bold">
                      Call Us
                    </h3>

                    <a
                      href="tel:+919876543210"
                      className="mt-1 block text-base font-semibold hover:text-[#087f4f]"
                    >
                      +91 98765 43210

                    </a>

                    <p className="mt-1 text-sm text-gray-500">
                      Mon - Sat (9:00 AM - 6:00 PM)
                    </p>

                  </div>


                  {/* Email */}

                  <div className="contact-card rounded-xl border border-gray-200 bg-white p-5 shadow-sm">

                    <div className="contact-icon flex h-11 w-11 items-center justify-center rounded-full bg-[#087f4f] text-white">
                      <Mail size={21} />
                    </div>

                    <h3 className="mt-4 font-bold">
                      Email Us
                    </h3>

                    <a
                      href="mailto:info@polybionictechnologies.com"
                      className="mt-1 block break-all text-sm font-semibold hover:text-[#087f4f]"
                    >
                      info@polybionictechnologies.com
                    </a>

                    <p className="mt-1 text-sm text-gray-500">
                      We respond within 24 hours
                    </p>

                  </div>


                  {/* Address */}

                  <div className="contact-card rounded-xl border border-gray-200 bg-white p-5 shadow-sm">

                    <div className="contact-icon flex h-11 w-11 items-center justify-center rounded-full bg-[#087f4f] text-white">
                      <MapPin size={21} />
                    </div>

                    <h3 className="mt-4 font-bold">
                      Visit Our Office
                    </h3>

                    <p className="mt-1 text-sm leading-6 text-gray-600">
                      Mohali,
                      <br />
                       Punjab, India
                    </p>

                    <p className="mt-1 text-xs text-gray-500">
                      By Appointment Only
                    </p>

                  </div>


                  {/* Social */}

                  <div className="contact-card rounded-xl border border-gray-200 bg-white p-5 shadow-sm">

                    <div className="contact-icon flex h-11 w-11 items-center justify-center rounded-full bg-[#087f4f] text-white">
                      <Globe size={21} />
                    </div>

                    <h3 className="mt-4 font-bold">
                      Follow Us
                    </h3>

                    <div className="mt-3 flex gap-3">

                      {["f", "◎", "in", "▶"].map(
                        (icon, index) => (

                          <a
                            key={index}
                            href="#"
                            className="flex h-8 w-8 items-center justify-center rounded-full text-sm font-bold text-gray-800 transition-all duration-300 hover:-translate-y-1 hover:bg-[#087f4f] hover:text-white hover:shadow-md"
                          >
                            {icon}
                          </a>

                        )
                      )}

                    </div>

                    <p className="mt-2 text-xs text-gray-500">
                      Stay connected for latest updates
                    </p>

                  </div>

                </div>

              </div>


              {/* =================================================
                  FORM
              ================================================= */}

              <div className="form-card rounded-2xl bg-[#f1faf5] p-6 lg:p-7">

                <p className="text-xs font-bold uppercase tracking-[0.12em] text-[#16854f]">
                  Send Us a Message
                </p>

                <h2 className="mt-1 text-3xl font-extrabold">
                  Enquiry Form
                </h2>

                <p className="mt-1 text-sm text-gray-600">
                  Fill in your details and our team will get back
                  to you shortly.
                </p>


                {submitted ? (

                  <div className="success-box mt-8 rounded-xl border border-green-200 bg-white p-7 text-center">

                    <div className="success-icon mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-green-100 text-[#087f4f]">
                      <Check size={28} />
                    </div>

                    <h3 className="mt-4 text-xl font-bold">
                      Enquiry Submitted!
                    </h3>

                    <p className="mt-2 text-sm text-gray-600">
                      Thank you for contacting Polybionic
                      Technologies. Our team will contact you
                      shortly.
                    </p>

                  </div>

                ) : (

                  <form
                    onSubmit={handleSubmit}
                    className="mt-6"
                  >

                    {/* Name / Email / Phone / Company */}

                    <div className="grid gap-3 sm:grid-cols-2">


                      {/* Name */}

                      <div className="relative">

                        <Users
                          size={17}
                          className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"
                        />

                        <input
                          type="text"
                          name="name"
                          value={formData.name}
                          onChange={handleChange}
                          placeholder="Full Name *"
                          required
                          className="h-12 w-full rounded-lg border border-gray-200 bg-white pl-10 pr-3 text-sm outline-none transition-all duration-300 focus:-translate-y-0.5 focus:border-[#087f4f] focus:ring-2 focus:ring-[#087f4f]/20"
                        />

                      </div>


                      {/* Email */}

                      <div className="relative">

                        <Mail
                          size={17}
                          className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"
                        />

                        <input
                          type="email"
                          name="email"
                          value={formData.email}
                          onChange={handleChange}
                          placeholder="Email Address *"
                          required
                          className="h-12 w-full rounded-lg border border-gray-200 bg-white pl-10 pr-3 text-sm outline-none transition-all duration-300 focus:-translate-y-0.5 focus:border-[#087f4f] focus:ring-2 focus:ring-[#087f4f]/20"
                        />

                      </div>


                      {/* Phone */}

                      <div className="relative">

                        <Phone
                          size={17}
                          className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"
                        />

                        <input
                          type="tel"
                          name="phone"
                          value={formData.phone}
                          onChange={handleChange}
                          placeholder="Phone Number *"
                          required
                          className="h-12 w-full rounded-lg border border-gray-200 bg-white pl-10 pr-3 text-sm outline-none transition-all duration-300 focus:-translate-y-0.5 focus:border-[#087f4f] focus:ring-2 focus:ring-[#087f4f]/20"
                        />

                      </div>


                      {/* Company */}

                      <div className="relative">

                        <Settings
                          size={17}
                          className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"
                        />

                        <input
                          type="text"
                          name="company"
                          value={formData.company}
                          onChange={handleChange}
                          placeholder="Company / Organization"
                          className="h-12 w-full rounded-lg border border-gray-200 bg-white pl-10 pr-3 text-sm outline-none transition-all duration-300 focus:-translate-y-0.5 focus:border-[#087f4f] focus:ring-2 focus:ring-[#087f4f]/20"
                        />

                      </div>

                    </div>


                    {/* Enquiry Type */}

                    <div className="mt-3">

                      <select
                        name="enquiryType"
                        value={formData.enquiryType}
                        onChange={handleChange}
                        required
                        className="h-12 w-full appearance-none rounded-lg border border-gray-200 bg-white px-3 text-sm text-gray-600 outline-none transition-all duration-300 focus:-translate-y-0.5 focus:border-[#087f4f] focus:ring-2 focus:ring-[#087f4f]/20"
                      >

                        <option value="">
                          Select enquiry type
                        </option>

                        {enquiryTypes.map((type) => (

                          <option
                            key={type}
                            value={type}
                          >
                            {type}
                          </option>

                        ))}

                      </select>

                    </div>


                    {/* Message */}

                    <div className="mt-3">

                      <textarea
                        name="message"
                        value={formData.message}
                        onChange={handleChange}
                        required
                        rows="5"
                        placeholder={`Your Message / Project Details *
Tell us about your requirements...`}
                        className="w-full resize-none rounded-lg border border-gray-200 bg-white px-3 py-3 text-sm outline-none transition-all duration-300 focus:-translate-y-0.5 focus:border-[#087f4f] focus:ring-2 focus:ring-[#087f4f]/20"
                      />

                    </div>


                    {/* Consent */}

                    <label className="mt-3 flex cursor-pointer items-start gap-2 text-xs text-gray-600">

                      <input
                        type="checkbox"
                        name="consent"
                        checked={formData.consent}
                        onChange={handleChange}
                        className="mt-0.5 accent-[#087f4f]"
                      />

                      <span>
                        I agree to be contacted by Polybionic
                        Technologies for this enquiry.
                      </span>

                    </label>


                    {/* Submit */}

                    <button
                      type="submit"
                      className="group mt-4 flex h-12 w-full items-center justify-center gap-2 rounded-full bg-[#087f4f] text-sm font-bold text-white shadow-sm transition-all duration-300 hover:-translate-y-1 hover:bg-[#066b42] hover:shadow-lg"
                    >

                      Submit Enquiry

                      <ArrowRight
                        size={17}
                        className="transition-transform duration-300 group-hover:translate-x-1"
                      />

                    </button>

                  </form>

                )}

              </div>

            </div>

          </div>

        </section>


        {/* =====================================================
            LOCATION
        ===================================================== */}

       <section className="reveal pb-14 lg:pb-16">
  <div className="mx-auto max-w-[1400px] px-5 lg:px-10">
    <div className="grid gap-8 lg:grid-cols-[1.05fr_0.95fr]">

      {/* =====================================================
          MAP
      ===================================================== */}
      <div>
        <div className="flex items-center gap-3">
          <div className="flex h-10 w-10 items-center justify-center rounded-full bg-[#dcf8e7] text-[#087f4f]">
            <MapPin size={20} />
          </div>

          <h2 className="text-2xl font-extrabold sm:text-3xl">
            Our Location
          </h2>
        </div>

        <p className="mt-2 text-sm leading-6 text-gray-600">
          Visit our office or find us on the map. We are located
          at Oxford Street, Zirakpur, Punjab, India, with easy
          access and good connectivity.
        </p>

        <div className="map-card relative mt-5 h-[330px] overflow-hidden rounded-2xl border border-gray-200 bg-[#eef4ef] shadow-sm">

          {/* GOOGLE MAP */}
          <iframe
            title="Polybionic Technologies Location"
            src="https://www.google.com/maps?q=Oxford+Street,+Zirakpur,+Punjab,+India&output=embed"
            className="map-frame h-full w-full border-0"
            loading="lazy"
            referrerPolicy="no-referrer-when-downgrade"
          />

          {/* MAP OVERLAY */}
          <div className="pointer-events-none absolute inset-0 rounded-2xl ring-1 ring-inset ring-black/5" />

          {/* LOCATION BADGE */}
          <div className="absolute left-4 top-4 max-w-[280px] rounded-xl bg-white/95 px-4 py-4 shadow-xl backdrop-blur-md">

            <div className="flex items-start gap-3">
              <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-full bg-[#dcf8e7] text-[#087f4f]">
                <MapPin size={20} />
              </div>

              <div>
                <p className="text-sm font-bold text-gray-900">
                  Polybionic Technologies
                </p>

                <p className="mt-1 text-xs leading-5 text-gray-500">
                   Oxford Street
                  <br />
                  Zirakpur, Punjab, India
                </p>
              </div>
            </div>

            {/* LOCATION */}
            <div className="mt-3 flex items-center gap-2 border-t border-gray-100 pt-3">
              <Globe
                size={14}
                className="text-[#087f4f]"
              />

              <span className="text-xs font-medium text-[#087f4f]">
                Zirakpur, Punjab
              </span>
            </div>

            {/* VIEW MAP */}
            <a
              href="https://www.google.com/maps/search/?api=1&query=%2C%20Oxford%20Street%2C%20Zirakpur%2C%20Punjab%2C%20India"
              target="_blank"
              rel="noreferrer"
              className="mt-3 flex items-center gap-1 text-xs font-semibold text-blue-600 transition hover:text-blue-800"
            >
              View larger map
              <ArrowRight
                size={13}
                className="transition-transform duration-300 hover:translate-x-1"
              />
            </a>
          </div>

          {/* LOCATION LABEL */}
          <div className="absolute bottom-4 right-4 hidden rounded-full bg-[#034d32]/90 px-4 py-2 text-xs font-semibold text-white shadow-lg backdrop-blur-sm sm:block">
            <span className="flex items-center gap-2">
              <MapPin size={13} />
              Zirakpur, Punjab
            </span>
          </div>
        </div>
      </div>


      {/* =====================================================
          OFFICE INFORMATION
      ===================================================== */}
      <div className="overflow-hidden rounded-2xl bg-[#f1faf5] shadow-sm">

        <div className="grid md:grid-cols-[1fr_0.9fr]">

          {/* OFFICE DETAILS */}
          <div className="p-7">

            <div className="flex items-center gap-2">
              <div className="flex h-9 w-9 items-center justify-center rounded-full bg-[#dcf8e7] text-[#087f4f]">
                <Building2 size={17} />
              </div>

              <p className="text-sm font-bold text-[#087f4f]">
                Office Information
              </p>
            </div>

            <p className="mt-2 text-sm leading-6 text-gray-600">
              Get in touch or visit us at our headquarters.
            </p>


            <div className="mt-6 space-y-5">

              {/* ADDRESS */}
              <div className="group flex gap-3">
                <div className="mt-1 flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-full bg-white text-[#087f4f] shadow-sm transition duration-300 group-hover:scale-110 group-hover:bg-[#087f4f] group-hover:text-white">
                  <MapPin size={18} />
                </div>

                <div>
                  <p className="text-sm font-bold">
                    Address
                  </p>

                  <p className="mt-1 text-sm leading-6 text-gray-600">
                    Oxford Street
                    <br />
                    Zirakpur, Punjab, India
                  </p>
                </div>
              </div>


              {/* PHONE */}
              <div className="group flex gap-3">
                <div className="mt-1 flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-full bg-white text-[#087f4f] shadow-sm transition duration-300 group-hover:scale-110 group-hover:bg-[#087f4f] group-hover:text-white">
                  <Phone size={18} />
                </div>

                <div>
                  <p className="text-sm font-bold">
                    Phone
                  </p>

                  <a
                    href="tel:+919876543210"
                    className="mt-1 block text-sm text-gray-600 transition hover:text-[#087f4f]"
                  >
                    +91 98765 43210
                  </a>
                </div>
                
              </div>
                        {/* PHONE */}
              <div className="group flex gap-3">
                <div className="mt-1 flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-full bg-white text-[#087f4f] shadow-sm transition duration-300 group-hover:scale-110 group-hover:bg-[#087f4f] group-hover:text-white">
                  <Phone size={18} />
                </div>

                <div>
                  <p className="text-sm font-bold">
                    Telephone
                  </p>

                  <a
                    href="tel:0172 4561505"
                    className="mt-1 block text-sm text-gray-600 transition hover:text-[#087f4f]"
                  >
                    0172 4561505
                  </a>
                </div>
                
              </div>



              {/* EMAIL */}
              <div className="group flex gap-3">
                <div className="mt-1 flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-full bg-white text-[#087f4f] shadow-sm transition duration-300 group-hover:scale-110 group-hover:bg-[#087f4f] group-hover:text-white">
                  <Mail size={18} />
                </div>

                <div className="min-w-0">
                  <p className="text-sm font-bold">
                    Email
                  </p>

                  <a
                    href="mailto:info@polybionictechnologies.com"
                    className="mt-1 block break-all text-sm text-gray-600 transition hover:text-[#087f4f]"
                  >
                    info@polybionictechnologies.com
                  </a>
                </div>
              </div>


              {/* WORKING HOURS */}
              <div className="group flex gap-3">
                <div className="mt-1 flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-full bg-white text-[#087f4f] shadow-sm transition duration-300 group-hover:scale-110 group-hover:bg-[#087f4f] group-hover:text-white">
                  <Clock3 size={18} />
                </div>

                <div>
                  <p className="text-sm font-bold">
                    Working Hours
                  </p>

                  <p className="mt-1 text-sm leading-6 text-gray-600">
                    Monday - Saturday
                    <br />
                    9:00 AM - 6:00 PM
                  </p>
                </div>
              </div>

            </div>


            {/* GET DIRECTIONS */}
            <a
              href="https://www.google.com/maps/dir/?api=1&destination=234%2C%20Oxford%20Street%2C%20Zirakpur%2C%20Punjab%2C%20India"
              target="_blank"
              rel="noreferrer"
              className="group mt-7 inline-flex items-center gap-2 rounded-full bg-[#087f4f] px-6 py-3 text-sm font-bold text-white transition-all duration-300 hover:-translate-y-1 hover:bg-[#066b42] hover:shadow-lg"
            >
              <MapPin size={16} />

              Get Directions

              <ArrowRight
                size={16}
                className="transition-transform duration-300 group-hover:translate-x-1"
              />
            </a>

          </div>


          {/* =====================================================
              OFFICE IMAGE
          ===================================================== */}
          <div className="image-container min-h-[350px] overflow-hidden">
            <img
              src={OfficeImage}
              alt="Polybionic Technologies office"
              className="office-image h-full w-full object-cover"
            />
          </div>

        </div>
      </div>

    </div>
  </div>
</section>

        {/* =====================================================
            WHY CONTACT US
        ===================================================== */}

        <section className="reveal pb-14 lg:pb-16">

          <div className="mx-auto max-w-[1400px] px-5 lg:px-10">

            <div className="grid gap-8 lg:grid-cols-[1fr_350px]">

              <div>

                <h2 className="text-2xl font-extrabold sm:text-3xl">
                  Why Contact Us?
                </h2>

                <p className="mt-1 text-sm text-gray-600">
                  We provide complete support from consultation
                  to project implementation.
                </p>


                <div className="mt-7 grid gap-6 sm:grid-cols-2 lg:grid-cols-4">

                  {benefits.map((item, index) => (

                    <div
                      key={index}
                      className="benefit-card text-center"
                      style={{
                        animationDelay: `${index * 0.15}s`,
                      }}
                    >

                      <div className="benefit-icon mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-green-100 text-[#087f4f]">
                        {item.icon}
                      </div>

                      <h3 className="mt-4 text-sm font-bold">
                        {item.title}
                      </h3>

                      <p className="mt-2 text-xs leading-5 text-gray-500">
                        {item.text}
                      </p>

                    </div>

                  ))}

                </div>

              </div>


              {/* Consultation */}

              <div className="consultation-card rounded-2xl bg-[#f1faf5] p-6">

                <div className="flex items-start gap-4">

                  <div className="consultation-icon flex h-14 w-14 flex-shrink-0 items-center justify-center rounded-full bg-[#087f4f] text-white">
                    <CalendarDays size={27} />
                  </div>

                  <div>

                    <h3 className="text-lg font-extrabold">
                      Book a Free Consultation
                    </h3>

                    <p className="mt-2 text-sm leading-6 text-gray-600">
                      Schedule a free consultation with our experts
                      to discuss your farming project, business idea
                      or custom requirements.
                    </p>

                    <a
                      href="#enquiry"
                      className="group mt-5 inline-flex items-center gap-2 rounded-full bg-[#087f4f] px-6 py-3 text-sm font-bold text-white transition-all duration-300 hover:-translate-y-1 hover:bg-[#066b42] hover:shadow-lg"
                    >

                      Book a Consultation

                      <ArrowRight
                        size={16}
                        className="transition-transform duration-300 group-hover:translate-x-1"
                      />

                    </a>

                  </div>

                </div>

              </div>

            </div>

          </div>

        </section>


        {/* =====================================================
            CTA
        ===================================================== */}

        <section className="cta-section relative overflow-hidden bg-[#005536] py-10">

          <div className="absolute inset-0 opacity-20">

            <div className="cta-orb absolute -left-10 -top-20 h-60 w-60 rounded-full bg-green-300 blur-3xl" />

            <div className="cta-orb-reverse absolute -bottom-20 right-0 h-72 w-72 rounded-full bg-green-400 blur-3xl" />

          </div>


          <div className="relative mx-auto flex max-w-[1400px] flex-col items-center justify-between gap-5 px-5 text-center md:flex-row md:px-10 md:text-left">

            <div>

              <p className="text-xs font-bold uppercase tracking-[0.15em] text-green-300">
                Let's Build Together
              </p>

              <h2 className="mt-2 text-2xl font-extrabold text-white sm:text-3xl">
                Ready to Start Your Farming Journey?
              </h2>

              <p className="mt-2 text-sm text-white/75">
                Talk to our experts and find the right solution
                for your goals.
              </p>

            </div>


            <a
              href="#enquiry"
              className="group inline-flex items-center gap-2 rounded-full bg-white px-7 py-3.5 text-sm font-bold text-[#087f4f] transition-all duration-300 hover:-translate-y-1 hover:bg-green-50 hover:shadow-xl"
            >

              Get a Free Consultation

              <ArrowRight
                size={16}
                className="transition-transform duration-300 group-hover:translate-x-1"
              />

            </a>

          </div>

        </section>

      </main>




    </>
  );
}